/*
 * Copyright (c) 2020
 * Company: G-Portal
 * Filename: actions.ts
 * Class: actions.ts
 * Author: Karl Magerl<karl@g-portal.com>
 * Version:
 */

import { ActionTree } from 'vuex';
import { AxiosResponse } from 'axios';
import {
  ChangeLagoPaymentMethodRequest,
  DeleteLagoPaymentMethodRequest,
  FetchLagoPaymentMethodsResponse, PaymentMethod, PaymentMethodData,
  PaymentMethodFetchPayload,
  PaymentMethodsState,
} from './types';
import { RootState } from '../types';
import { ApiEndpointEnum, getApiRequestConfig } from '../../config/network';
import { BaseMoney } from '../price/types';

const getFetchPaymentMethodPrefix = (mode: string) => {
  let mutationPrefix: string = null;

  switch (mode) {
    case 'recharge':
      mutationPrefix = 'rechargeMethods';
      break;

    case 'checkout':
      mutationPrefix = 'checkoutMethods';
      break;

    case 'setup':
      mutationPrefix = 'setupMethods';
      break;

    default:
      throw new Error(`fetchMethods cannot process mode ${mode}!`);
  }

  return mutationPrefix;
};

export const actions: ActionTree<PaymentMethodsState, RootState> = {
  fetchPreferableAmount({ commit }): void {
    commit('preferableAmountLoading');

    this.state.$http.get('minimumTransaction', getApiRequestConfig(ApiEndpointEnum.PaymentAPI))
      .then((response) => {
        const payload: BaseMoney = response && response.data;
        commit('preferableAmountLoaded', payload);
      }, (error) => {
        commit('preferableAmountError', error);
      });
  },
  fetchPaymentMethods({ commit }, payload: PaymentMethodFetchPayload): Promise<PaymentMethodData> {
    const mutationPrefix: string = getFetchPaymentMethodPrefix(payload.mode);

    commit(`${mutationPrefix}Loading`);

    return new Promise((resolve, reject) => {
      this.state.$http.post('methods', payload, getApiRequestConfig(ApiEndpointEnum.PaymentAPI))
        .then((response: AxiosResponse<PaymentMethodData>) => {
          const paymentMethodData: PaymentMethodData = response && response.data;
          commit(`${mutationPrefix}Loaded`, paymentMethodData);
          resolve(paymentMethodData);
        }, (error) => {
          commit(`${mutationPrefix}Error`);
          reject(error);
        });
    });
  },

  async fetchAdyenSPM({ commit }): Promise<PaymentMethod> {
    try {
      commit('adyenSPMLoading');

      const {
        data,
        status,
        statusText,
      } = await this.state.$http.get<PaymentMethod>(`${window.regionPrefix}/order/payment_api/getAdyenSPM`);

      if (status === 200) {
        commit('adyenSPMLoaded', data);
        return data;
      }
      if (status > 200 && statusText != null) {
        commit('adyenSPMError');
        throw new Error(statusText);
      }
    } catch (error) {
      throw new Error(error);
    }

    return null;
  },

  async fetchLagoPaymentMethods({ commit }): Promise<FetchLagoPaymentMethodsResponse> {
    try {
      const {
        data,
        status,
        statusText,
      } = await this.state.$http.get<FetchLagoPaymentMethodsResponse>(`${window.regionPrefix}/payment/stripe/paymentMethods`);
      commit('setLagoPaymentMethods', data.methods);
      commit('setDefaultLagoPaymentMethod', data.default_payment_method);

      if (status === 200) {
        return data;
      }
      if (status > 200 && statusText != null) {
        throw new Error(statusText);
      }
    } catch (error) {
      throw new Error(error);
    }

    return null;
  },

  async changeLagoPaymentMethod(
    { commit },
    {
      id: paymentMethodId,
      notify = false,
      commitState = true,
    }: ChangeLagoPaymentMethodRequest,
  ): Promise<void> {
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    try {
      const {
        status,
        statusText,
      } = await this.state.$http.post(`${window.regionPrefix}/payment/stripe/changePaymentMethod`, undefined,
        paymentMethodId != null
          ? {
            headers,
            params: {
              payment_method_id: paymentMethodId,
              notify: notify ? 'true' : 'false',
            },
          } : {
            headers,
          });

      if (status > 200 && statusText != null) {
        throw new Error(statusText);
      }

      // can be null if we want to reset the default payment method
      if (commitState) {
        commit('setDefaultLagoPaymentMethod', paymentMethodId);
      }
    } catch (error) {
      throw new Error(error);
    }
  },

  async deleteLagoPaymentMethod(
    { commit, state },
    {
      id: paymentMethodId,
      notify = false,
    }: DeleteLagoPaymentMethodRequest,
  ): Promise<void> {
    try {
      const {
        status,
        statusText,
      } = await this.state.$http.delete(`${window.regionPrefix}/payment/stripe/deletePaymentMethod`, {
        params: {
          payment_method_id: paymentMethodId,
          notify: notify ? 'true' : 'false',
        },
      });

      if (status > 200 && statusText != null) {
        throw new Error(statusText);
      }

      commit('deleteLagoPaymentMethod', paymentMethodId);

      // if the deleted payment method was the default payment method, we have to set it to null
      if (paymentMethodId === state.lagoDefaultPaymentMethod) {
        commit('setDefaultLagoPaymentMethod', null);
      }
    } catch (error) {
      throw new Error(error);
    }
  },
};
